import { useMemo } from 'react';
import parsePhoneNumberFromString from 'libphonenumber-js';

import { sanitizeNationalNumber } from '../../logic/sanitizeNationalNumber';
import { stripCountryCallingCode } from '../../logic/stripCountryCallingCode';
import { PhoneInputProps } from '../../types/PhoneInputProps';
import { usePhoneCountries } from '../usePhoneCountries';

export const useParsedPhoneNumber = (value: PhoneInputProps['value']) => {
  const { countries, defaultCountry } = usePhoneCountries();

  const parsedPhoneNumber = useMemo(
    () => parsePhoneNumberFromString(value ?? '') ?? undefined,
    [value],
  );

  const countryCallingCode = useMemo(() => {
    const countryNumber =
      parsedPhoneNumber?.countryCallingCode || defaultCountry?.value;
    if (!countryNumber) {
      return '';
    }

    return (
      countries.find((country) => country.value === countryNumber)?.value ||
      defaultCountry?.value ||
      ''
    );
  }, [countries, parsedPhoneNumber?.countryCallingCode, defaultCountry?.value]);

  const nationalNumber = useMemo(() => {
    if (parsedPhoneNumber?.nationalNumber) {
      return sanitizeNationalNumber(parsedPhoneNumber.nationalNumber);
    }

    const digitsOnly = sanitizeNationalNumber(value);
    const withoutCallingCode = stripCountryCallingCode(
      digitsOnly,
      countryCallingCode,
    );
    return sanitizeNationalNumber(withoutCallingCode);
  }, [countryCallingCode, parsedPhoneNumber?.nationalNumber, value]);

  return {
    countryCallingCode,
    nationalNumber,
  };
};
